import '../styles/globals.css'
import '@fortawesome/fontawesome-svg-core/styles.css'
import * as Sentry from '@sentry/nextjs'
import { SessionProvider } from "next-auth/react"
import type { Session } from 'next-auth'
import type { AppProps } from 'next/app'
import type { NextPage } from 'next'
import type { ReactElement, ReactNode } from 'react'
import localFont from 'next/font/local'
import Script from "next/script"
import { Router } from 'next/router'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryClient } from '@/lib/query-client'
import { UpgradeProvider, useUpgrade } from '@/contexts/UpgradeContext'
import { ToastProvider } from '@/contexts/ToastContext'
import { UserProvider, useUser } from '@/contexts/UserContext'
import { AccountProvider } from '@/contexts/AccountContext'
import { SearchRefreshProvider } from '@/contexts/SearchRefreshContext'
import UpgradeModal from '@/components/features/modals/UpgradeModal'
import ToastContainer from '@/components/common/ToastContainer'
import { installDomSafetyPatches } from '@/utils/domSafety'

// Keep React from crashing when Google Translate rewrites the DOM under it
// (NotFoundError: removeChild/insertBefore). Module scope so it runs before
// the first render, browser only.
installDomSafetyPatches()

// Self-hosted via next/font (no external request). Exposed as --font-sans so
// Tailwind's `sans` stack — which preflight applies to <html> — picks it up
// everywhere, including portaled modals/toasts.
// App UI typeface: PP Neue Montreal Text (Pangram Pangram) — the small-size cut.
// Licensed production variable (Upright Family v3.0, wght 200–700), self-hosted
// via next/font and exposed as --font-sans so Tailwind's `sans` stack (applied
// to <html> by preflight) carries it everywhere, portaled modals/toasts included.
const ppNeueMontrealText = localFont({ src: '../fonts/PPNeueMontrealText-Variable.woff2' })

type ReceiptsAppPageProps = AppProps['pageProps'] & {
  session?: Session | null;
  user?: unknown;
};

// Pages opt into the persistent shell by exporting `getLayout`, which wraps the
// page content in <Layout>. Because _app renders the same Layout element across
// navigations, React keeps the sidebar mounted and only swaps the page content.
export type NextPageWithLayout<P = Record<string, unknown>> = NextPage<P> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppWithUpgradeModalProps = {
  Component: NextPageWithLayout;
  pageProps: ReceiptsAppPageProps;
};

// Identify the user with Crisp so support knows who is chatting. Must render
// inside UserProvider (useUser above it only sees the context default: null).
// $crisp acts as a command queue before the lazy script loads — Crisp replays
// queued commands on load — so pushing before load is safe as long as the
// loader preserves an existing queue.
function CrispUserSync() {
  const { user } = useUser()
  useEffect(() => {
    if (!user?.email) return
    window.$crisp = window.$crisp || []
    window.$crisp.push(['set', 'user:email', [user.email]])
    if (user.name) {
      window.$crisp.push(['set', 'user:nickname', [user.name]])
    }
    if (user.company_name) {
      window.$crisp.push(['set', 'user:company', [user.company_name]])
    }
    // Extra metadata visible to agents in the visitor sidebar. Crisp only
    // accepts session:data as [key, value] pairs with scalar values — a bare
    // object (or an undefined value) makes the client throw "Invalid data".
    window.$crisp.push(['set', 'session:data', [[
      ['plan', String(user.plan ?? 'unknown')],
      ['subscribed', user.subscribed === true],
    ]]])
  }, [user])
  return null
}

// Component that uses the upgrade context
function AppWithUpgradeModal({ Component, pageProps }: AppWithUpgradeModalProps) {
  const { isUpgradeModalOpen, upgradeReason, upgradeHighlight, closeUpgradeModal, openUpgradeModal } = useUpgrade()
  const router = useRouter()
  const getLayout = Component.getLayout ?? ((page: ReactElement) => page)

  // `?upgrade=1` on any page opens the plan picker (the flag is stripped from
  // the URL afterwards). The /upgrade route is handled by pages/upgrade.tsx,
  // which also carries the marketing `coupon_id`; opening it here as well
  // would reset that coupon to null.
  useEffect(() => {
    if (!router.isReady) return
    const fromQuery = typeof router.query.upgrade === 'string' && router.query.upgrade
    if (!fromQuery) return
    openUpgradeModal()
    const { upgrade: _upgrade, ...rest } = router.query
    router.replace({ pathname: router.pathname, query: rest }, undefined, { shallow: true })
  }, [router.isReady, router.pathname, router.query.upgrade, openUpgradeModal, router])

  return (
    <UserProvider initialUser={pageProps.user}>
      <CrispUserSync />
      <AccountProvider>
        <SearchRefreshProvider>
          {getLayout(<Component {...pageProps} />)}
          <UpgradeModal
            open={isUpgradeModalOpen}
            setOpen={closeUpgradeModal}
            upgradeReason={upgradeReason}
            upgradeHighlight={upgradeHighlight}
          />
          <ToastContainer />
        </SearchRefreshProvider>
      </AccountProvider>
    </UserProvider>
  )
}

export default function App(props: AppProps<ReceiptsAppPageProps>) {
    // When getServerSideProps throws and triggers a redirect, Next.js can invoke
    // App with undefined props/Component. Destructure in the body (not the
    // signature) so that render returns null instead of crashing.
    const { Component } = props ?? {}
    const { session, ...pageProps } = props?.pageProps ?? {}
    const DEV_MODE = process.env.NEXT_PUBLIC_DEV_MODE === 'true'
    const MICROSOFT_CLARITY_ID = process.env.NEXT_PUBLIC_MICROSOFT_CLARITY_ID
    // Crisp is env-gated: unset NEXT_PUBLIC_CRISP_WEBSITE_ID = widget hidden
    // (temporarily disabled for the prod cutover; re-enable via .env).
    const CRISP_WEBSITE_ID = process.env.NEXT_PUBLIC_CRISP_WEBSITE_ID
    const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY
    const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST
    const HOST_URL = process.env.NEXT_PUBLIC_HOST_URL
    
    // New environment variables for granular control
    const ENABLE_ANALYTICS = process.env.NEXT_PUBLIC_ENABLE_ANALYTICS === 'true'
    const ENABLE_DEV_TOOLS = process.env.NEXT_PUBLIC_ENABLE_DEV_TOOLS === 'true'

    useEffect(() => {
      if (POSTHOG_KEY && POSTHOG_HOST && ENABLE_ANALYTICS && HOST_URL !== 'http://localhost:3000') {
        posthog.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          person_profiles: 'identified_only',
          loaded: (posthog) => {
            if (DEV_MODE) posthog.debug()
          }
        })

        const handleRouteChange = () => posthog.capture('$pageview')

        Router.events.on('routeChangeComplete', handleRouteChange)

        return () => {
          Router.events.off('routeChangeComplete', handleRouteChange)
        }
      }
    },[])

    // Keep the Crisp chat bubble hidden after every SPA route change.
    // Crisp reinitializes on navigation, which re-shows the bubble by default.
    useEffect(() => {
      const hideCrisp = () => {
        if (typeof window !== 'undefined' && window.$crisp) {
          window.$crisp.push(['do', 'chat:hide'])
        }
      }

      Router.events.on('routeChangeComplete', hideCrisp)
      return () => Router.events.off('routeChangeComplete', hideCrisp)
    },[])

    // Determine if we should show analytics scripts
    const shouldShowAnalytics = ENABLE_ANALYTICS || (!DEV_MODE && process.env.NEXT_PUBLIC_ENABLE_ANALYTICS !== 'false')
    
    // Determine if we should show dev tools
    const shouldShowDevTools = ENABLE_DEV_TOOLS || (DEV_MODE && process.env.NEXT_PUBLIC_ENABLE_DEV_TOOLS !== 'false')

    // SSR failed mid-redirect — render nothing and let the redirect/error
    // handler proceed. (After the hooks, so the hook order stays stable.)
    if (!Component) {
      return null
    }

    return (
      <Sentry.ErrorBoundary
        fallback={
          <div className="flex flex-col items-center justify-center min-h-screen text-gray-600 p-8 text-center">
            <p className="text-lg font-medium">Something went wrong.</p>
            <p className="text-sm mt-2">The error has been reported. Please refresh the page.</p>
          </div>
        }
      >
      <style jsx global>{`
        :root {
          --font-sans: ${ppNeueMontrealText.style.fontFamily};
        }
      `}</style>
      <QueryClientProvider client={queryClient}>
        <PostHogProvider client={posthog}>
          <SessionProvider session={session}>
            <ToastProvider>
              <UpgradeProvider>
                <AppWithUpgradeModal Component={Component as NextPageWithLayout} pageProps={pageProps} />
            {shouldShowAnalytics && MICROSOFT_CLARITY_ID && (
              <Script strategy='lazyOnload' id='clarity-script'>
                {`
                  (function(c,l,a,r,i,t,y){
                    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                  })(window, document, "clarity", "script", "${MICROSOFT_CLARITY_ID}");
                `}
              </Script>
            )}

            {CRISP_WEBSITE_ID && (
              <Script strategy='lazyOnload' id='crisp-script'>
                {`
                  window.$crisp=window.$crisp||[];window.CRISP_WEBSITE_ID="${CRISP_WEBSITE_ID}";
                  (function(){var d=document,s=d.createElement("script");s.src="https://client.crisp.chat/l.js";s.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();
                  window.$crisp.push(["do","chat:hide"]);
                `}
              </Script>
            )}

            {shouldShowDevTools && (
              <>
                   <ReactQueryDevtools initialIsOpen={false} />
                   <Script
                      crossOrigin="anonymous"
                      src="//unpkg.com/react-scan/dist/auto.global.js"
                    />
              </>
            )}
              </UpgradeProvider>
            </ToastProvider>
          </SessionProvider>
     
        </PostHogProvider>
      </QueryClientProvider>
      </Sentry.ErrorBoundary>
    )
  }